import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmailTemplates, updateEmailTemplate, seedDefaults, createEmailTemplate, deleteEmailTemplate } from '../../../store/slices/emailTemplatesSlice';

const TRIGGER_META = {
  ticket_created:     { label: 'Ticket Created',                  color: 'green', icon: '🎫', recipients: ['Admins', 'Team Leads', 'Assigned Agent'] },
  l2_sme_aligned:     { label: 'L2 / SME Aligned',                color: 'blue',  icon: '👤', recipients: ['Admins', 'Team Leads', 'Assigned Agent', 'L2 / SME'] },
  ticket_resolved:    { label: 'Ticket Resolved',                  color: 'gray',  icon: '✅', recipients: ['Admins', 'Team Leads', 'Assigned Agent'] },
  rma_initiated:      { label: 'RMA Initiated',                    color: 'red',   icon: '🔄', recipients: ['Admins', 'Team Leads', 'Assigned Agent', 'Supply Chain'] },
  escalation_warning: { label: 'Escalation Warning (20 min)',      color: 'amber', icon: '⚠️', recipients: ['Admins', 'Team Leads', 'Assigned Agent'] },
  escalation_breach:  { label: 'Escalation Breach (30 min)',       color: 'red',   icon: '🚨', recipients: ['Admins', 'Team Leads', 'Assigned Agent'] },
  rma_child_created:  { label: 'RMA Child Ticket Created',         color: 'blue',  icon: '📦', recipients: ['Commercial'] },
};

const TRIGGER_VARIABLES = {
  ticket_created:     ['ticket_number','subject','location','device_name','serial_number','assigned_to','created_by','description'],
  l2_sme_aligned:     ['ticket_number','subject','location','device_name','serial_number','assigned_to','l2_sme'],
  ticket_resolved:    ['ticket_number','subject','location','device_name','serial_number','assigned_to'],
  rma_initiated:      ['ticket_number','subject','location','device_name','serial_number','assigned_to'],
  escalation_warning: ['ticket_number','subject','device_name','serial_number','location','assigned_to','created_at'],
  escalation_breach:  ['ticket_number','subject','device_name','serial_number','location','assigned_to','created_at'],
  rma_child_created:  ['ticket_number','subject','device_name','serial_number','location','assigned_to','created_at'],
};

const SAMPLE_DATA = {
  ticket_number: 'BHEL-2026-00042',
  subject: 'Video Conferencing System Not Working',
  location: 'BHEL Haridwar',
  device_name: 'Dell Optiplex Micro PC',
  serial_number: '96N6R24',
  assigned_to: 'Rajesh Kumar',
  created_by: 'Amit Singh',
  description: 'The VC system in Conference Room A is not powering on since morning.',
  l2_sme: 'Suresh Sharma (OEM Support)',
  created_at: '2026-07-23 09:15',
};

const COLOR_CLASSES = {
  green: { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-400', active: 'bg-emerald-50 border-l-4 border-emerald-500' },
  blue:  { badge: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500',    border: 'border-blue-400',    active: 'bg-blue-50 border-l-4 border-blue-500' },
  gray:  { badge: 'bg-gray-100 text-gray-700',        dot: 'bg-gray-500',    border: 'border-gray-400',    active: 'bg-gray-50 border-l-4 border-gray-500' },
  red:   { badge: 'bg-red-100 text-red-700',          dot: 'bg-red-500',     border: 'border-red-400',     active: 'bg-red-50 border-l-4 border-red-500' },
  amber: { badge: 'bg-amber-100 text-amber-700',      dot: 'bg-amber-500',   border: 'border-amber-400',   active: 'bg-amber-50 border-l-4 border-amber-500' },
};

const TRIGGER_ORDER = ['ticket_created','l2_sme_aligned','ticket_resolved','rma_initiated','escalation_warning','escalation_breach','rma_child_created'];

function insertAtCursor(el, text) {
  if (!el) return el.value;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const before = el.value.substring(0, start);
  const after = el.value.substring(end);
  const newVal = before + text + after;
  // Use native input setter to trigger React's onChange
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value') ||
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
  if (nativeInputValueSetter) {
    nativeInputValueSetter.set.call(el, newVal);
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  // Restore cursor after React re-render
  requestAnimationFrame(() => {
    el.selectionStart = el.selectionEnd = start + text.length;
    el.focus();
  });
  return newVal;
}

function renderPreview(html, subject) {
  let rendered = html;
  let renderedSubject = subject;
  Object.entries(SAMPLE_DATA).forEach(([k, v]) => {
    const re = new RegExp(`\\$\\{${k}\\}`, 'g');
    rendered = rendered.replace(re, v);
    renderedSubject = renderedSubject.replace(re, v);
  });
  return { renderedSubject, rendered };
}

export default function EmailTemplates() {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.emailTemplates);

  const [selectedTrigger, setSelectedTrigger] = useState('ticket_created');
  const [activeTab, setActiveTab] = useState('editor');
  const [localEdits, setLocalEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Create mode state
  const [createMode, setCreateMode] = useState(false);
  const [createForm, setCreateForm] = useState({ trigger: '', subject: '', body_html: '', is_active: true });
  const [createSaving, setCreateSaving] = useState(false);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocusedRef = useRef('body');

  useEffect(() => {
    dispatch(fetchEmailTemplates()).then(action => {
      if (action.payload && action.payload.length === 0) {
        dispatch(seedDefaults()).then(() => dispatch(fetchEmailTemplates()));
      }
    });
  }, [dispatch]);

  const currentTemplate = list.find(t => t.trigger === selectedTrigger);

  const edit = localEdits[selectedTrigger] || {};
  const subject = edit.subject !== undefined ? edit.subject : (currentTemplate?.subject || '');
  const body_html = edit.body_html !== undefined ? edit.body_html : (currentTemplate?.body_html || '');
  const is_active = edit.is_active !== undefined ? edit.is_active : (currentTemplate?.is_active ?? true);

  const setField = useCallback((field, value) => {
    setLocalEdits(prev => ({
      ...prev,
      [selectedTrigger]: { ...prev[selectedTrigger], [field]: value }
    }));
  }, [selectedTrigger]);

  const handleVarChipClick = (varName) => {
    const token = `\${${varName}}`;
    if (lastFocusedRef.current === 'subject' && subjectRef.current) {
      insertAtCursor(subjectRef.current, token);
    } else if (bodyRef.current) {
      insertAtCursor(bodyRef.current, token);
    }
  };

  const handleSave = async () => {
    if (!currentTemplate) return;
    setSaving(true);
    try {
      await dispatch(updateEmailTemplate({
        id: currentTemplate.id,
        data: { subject, body_html, is_active },
      })).unwrap();
      setLocalEdits(prev => { const n = { ...prev }; delete n[selectedTrigger]; return n; });
      setToast('Saved successfully');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast('Save failed');
      setTimeout(() => setToast(null), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentTemplate) return;
    if (!window.confirm(`Delete template for "${selectedTrigger}"? This cannot be undone.`)) return;
    try {
      await dispatch(deleteEmailTemplate(currentTemplate.id)).unwrap();
      setToast('Template deleted');
      setTimeout(() => setToast(null), 2500);
      // Select first available trigger
      const remaining = TRIGGER_ORDER.find(t => t !== selectedTrigger);
      if (remaining) setSelectedTrigger(remaining);
    } catch {
      setToast('Delete failed');
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleCreateSave = async () => {
    if (!createForm.trigger.trim()) return;
    setCreateSaving(true);
    try {
      await dispatch(createEmailTemplate(createForm)).unwrap();
      setToast('Template created');
      setTimeout(() => setToast(null), 2500);
      setCreateMode(false);
      setCreateForm({ trigger: '', subject: '', body_html: '', is_active: true });
      setSelectedTrigger(createForm.trigger.trim());
    } catch {
      setToast('Create failed');
      setTimeout(() => setToast(null), 2500);
    } finally {
      setCreateSaving(false);
    }
  };

  const meta = TRIGGER_META[selectedTrigger] || { label: selectedTrigger, color: 'gray', icon: '📧', recipients: [] };
  const vars = TRIGGER_VARIABLES[selectedTrigger] || [];
  const { renderedSubject, rendered } = renderPreview(body_html, subject);
  const isDirty = !!localEdits[selectedTrigger] && Object.keys(localEdits[selectedTrigger]).length > 0;

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Email Notification Templates</h1>
        <p className="text-gray-500 text-sm mt-1">Manage automated email notifications sent on ticket events.</p>
      </div>

      <div className="flex gap-5 flex-1 min-h-0">
        {/* Left sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-2">
          <div className="flex-1 space-y-2 overflow-y-auto">
            {TRIGGER_ORDER.map(trigger => {
              const m = TRIGGER_META[trigger];
              const tmpl = list.find(t => t.trigger === trigger);
              const c = COLOR_CLASSES[m.color];
              const isSelected = selectedTrigger === trigger && !createMode;
              const hasLocalEdit = !!localEdits[trigger] && Object.keys(localEdits[trigger]).length > 0;
              return (
                <button
                  key={trigger}
                  onClick={() => { setSelectedTrigger(trigger); setCreateMode(false); }}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all ${
                    isSelected
                      ? `bg-white shadow-md border-brand-300 ring-2 ring-brand-200`
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">{m.icon}</span>
                    <span className="text-sm font-semibold text-gray-800">{m.label}</span>
                    {hasLocalEdit && <span className="ml-auto w-2 h-2 rounded-full bg-amber-400" title="Unsaved changes" />}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {tmpl ? (tmpl.is_active ? 'Active' : 'Inactive') : 'Not set'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* New Template button */}
          <button
            onClick={() => { setCreateMode(true); setCreateForm({ trigger: '', subject: '', body_html: '', is_active: true }); }}
            className={`w-full px-4 py-2.5 rounded-xl border-2 border-dashed text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              createMode
                ? 'border-brand-400 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-500 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50'
            }`}
          >
            <span className="text-lg leading-none">+</span> New Template
          </button>
        </div>

        {/* Right panel */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col min-h-0 overflow-hidden">
          {createMode ? (
            /* Create form panel */
            <>
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <span className="text-xl">📧</span>
                <div>
                  <h2 className="font-bold text-gray-900 text-base">New Email Template</h2>
                  <p className="text-xs text-gray-400">Create a custom trigger template</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Trigger Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={createForm.trigger}
                    onChange={e => setCreateForm(f => ({ ...f, trigger: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 font-mono"
                    placeholder="e.g. custom_alert"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                  <input
                    type="text"
                    value={createForm.subject}
                    onChange={e => setCreateForm(f => ({ ...f, subject: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400"
                    placeholder="Email subject line…"
                  />
                </div>
                <div className="flex-1 flex flex-col">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Body HTML</label>
                  <textarea
                    value={createForm.body_html}
                    onChange={e => setCreateForm(f => ({ ...f, body_html: e.target.value }))}
                    className="flex-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 resize-none"
                    rows={14}
                    placeholder="<p>HTML email body…</p>"
                    spellCheck={false}
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={createForm.is_active}
                    onClick={() => setCreateForm(f => ({ ...f, is_active: !f.is_active }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${createForm.is_active ? 'bg-brand-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${createForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-gray-600 font-medium">Active</span>
                </label>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  onClick={() => setCreateMode(false)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSave}
                  disabled={createSaving || !createForm.trigger.trim()}
                  className="px-5 py-2 rounded-lg text-sm font-semibold bg-brand-600 hover:bg-brand-700 text-white shadow-sm disabled:opacity-50 transition-colors"
                >
                  {createSaving ? 'Creating…' : 'Create Template'}
                </button>
              </div>
            </>
          ) : loading && !currentTemplate ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">Loading templates…</div>
          ) : (
            <>
              {/* Panel header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{meta.icon}</span>
                  <div>
                    <h2 className="font-bold text-gray-900 text-base">{meta.label}</h2>
                    <p className="text-xs text-gray-400">Trigger: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{selectedTrigger}</code></p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Delete button */}
                  {currentTemplate && (
                    <button
                      onClick={handleDelete}
                      title="Delete template"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  {/* Active toggle */}
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-sm text-gray-600 font-medium">Active</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={is_active}
                      onClick={() => setField('is_active', !is_active)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${is_active ? 'bg-brand-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </label>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-3 border-b border-gray-100 flex gap-4">
                {['editor', 'preview'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-2 text-sm font-semibold capitalize border-b-2 transition-colors ${activeTab === tab ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                  >
                    {tab === 'editor' ? 'Editor' : 'Preview'}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                {/* Recipients */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 font-medium mr-1">Recipients:</span>
                  {meta.recipients.map(r => (
                    <span key={r} className="text-xs bg-purple-100 text-purple-700 px-2.5 py-0.5 rounded-full font-medium">{r}</span>
                  ))}
                </div>

                {activeTab === 'editor' ? (
                  <>
                    {/* Subject */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                      <input
                        ref={subjectRef}
                        type="text"
                        value={subject}
                        onFocus={() => { lastFocusedRef.current = 'subject'; }}
                        onChange={e => setField('subject', e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 font-mono"
                        placeholder="Email subject line…"
                      />
                    </div>

                    {/* Variable chips */}
                    {vars.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2">Insert variable <span className="font-normal text-gray-400">(click to insert at cursor)</span></p>
                        <div className="flex flex-wrap gap-1.5">
                          {vars.map(v => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => handleVarChipClick(v)}
                              className="text-xs px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md font-mono border border-indigo-200 transition-colors"
                            >
                              {`\${${v}}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Body HTML */}
                    <div className="flex-1 flex flex-col">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Body HTML</label>
                      <textarea
                        ref={bodyRef}
                        value={body_html}
                        onFocus={() => { lastFocusedRef.current = 'body'; }}
                        onChange={e => setField('body_html', e.target.value)}
                        className="flex-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 resize-none"
                        rows={16}
                        placeholder="<p>HTML email body…</p>"
                        spellCheck={false}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Preview */}
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <span className="text-xs text-gray-500 font-medium">Subject: </span>
                        <span className="text-sm text-gray-800 font-semibold">{renderedSubject}</span>
                      </div>
                      <div
                        className="p-5 bg-white min-h-[300px] text-sm"
                        dangerouslySetInnerHTML={{ __html: rendered }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 text-center">Preview rendered with sample data</p>
                  </>
                )}
              </div>

              {/* Footer — Save */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {currentTemplate?.updated_by_name
                    ? `Last updated by ${currentTemplate.updated_by_name}`
                    : 'Not yet saved'}
                </p>
                <button
                  onClick={handleSave}
                  disabled={saving || !currentTemplate}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    isDirty
                      ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-400 cursor-default'
                  } disabled:opacity-60`}
                >
                  {saving ? 'Saving…' : 'Save Template'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-5 py-3 rounded-xl shadow-lg text-sm font-semibold text-white transition-all z-50 ${toast.includes('fail') ? 'bg-red-500' : 'bg-emerald-500'}`}>
          {toast}
        </div>
      )}
    </div>
  );
}
